import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FORWARD_NOTE_LIMIT, FORWARD_NOTE_MAX_BODY, PREDICTION_MAX_BODY, type ClubBook, type ForwardNote, type Moment, type Prediction, type Predictions } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { posLabel, unitAt, type PosCtx } from "../lib/format";
import { Avatar, Sheet } from "./ui";
import { Cover } from "./BookBits";
import { Stars, StarPicker } from "./Stars";
import { ChevronIcon, CloseIcon } from "./Icons";
import { FinishLineCard } from "./FinishCards";
import { notes } from "@pagemates/shared/copy";

const txt = notes.moments;

/*
 * Unsealing moments, notes left forward and predictions on the web (PLAN.md
 * §Unsealing moments; mock artifact 9m6qHNfWzH2ZE4ykmBGVoq). Colour roles:
 * the lamp only for seals and your own spot, brass wax and stars on the
 * paper (a material, the same in both modes), moss for anything you tap.
 * Every motion here is under a second and skipped with reduced motion.
 */

/** "p. 96", "4h 10m", or "40%" when the book has no unit. */
export const spot = (percent: number, ctx: PosCtx) => unitAt(percent, ctx) || `${percent}%`;
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const day = (iso: string) => {
  const d = new Date(iso), now = new Date();
  if (d.toDateString() === now.toDateString()) return txt.today;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export function useReducedMotion() {
  const q = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
  const [reduced, setReduced] = useState(Boolean(q?.matches));
  useEffect(() => {
    if (!q) return;
    const on = () => setReduced(q.matches);
    q.addEventListener("change", on);
    return () => q.removeEventListener("change", on);
  }, [q]);
  return reduced;
}

/**
 * The viewer's waiting moment for a book. Worked out by the server when asked (opening the app), and asked
 * again whenever the viewer's own progress or status changes, so an update in the app shows its moment at once.
 */
export function useMoment(clubId: string, bookId: string | undefined, key: string) {
  return useQuery({ queryKey: ["moment", clubId, bookId, key], queryFn: () => api.moment(clubId, bookId!), enabled: Boolean(bookId), staleTime: Infinity });
}

/** A paper envelope sealed in brass wax; `count` > 1 draws a small stack. */
export function Envelope({ count = 1, size }: { count?: number; size?: "lg" }) {
  if (count > 1) return <span className="env-stack" aria-hidden="true"><span className="env" /><span className="env" /><span className="env" /></span>;
  return <span className={`env${size ? ` ${size}` : ""}`} aria-hidden="true" />;
}

// ---- the card at the top of the Reading tab

export function MomentCard({ clubId, book, ctx, moment }: { clubId: string; book: ClubBook; ctx: PosCtx; moment: Moment }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [envelopes, setEnvelopes] = useState(false);
  const dismiss = useMutation({ mutationFn: () => api.dismissMoment(clubId, book.id), onSuccess: () => qc.invalidateQueries({ queryKey: ["moment", clubId, book.id] }) });
  const opened = moment.messages + moment.questions;
  const since = spot(moment.fromPercent, ctx);
  const notePages = new Set(moment.notes.map((n) => n.position));
  const noteLine = moment.notes.length === 1 ? txt.aNoteWasWaiting(spot(moment.notes[0]!.position, ctx))
    : notePages.size === 1 ? txt.notesWereWaitingAt(moment.notes.length, spot(moment.notes[0]!.position, ctx))
    : txt.notesWereWaitingFor(moment.notes.length);
  const what = [moment.messages > 0 && plural(moment.messages, txt.message, txt.messages), moment.questions > 0 && plural(moment.questions, txt.meetingQuestion, txt.meetingQuestions)].filter(Boolean).join(txt.andSep);
  return (
    <section className="card moment" aria-label={txt.whatOpenedSinceYou}>
      <button type="button" className="moment-x" aria-label={txt.dismiss} disabled={dismiss.isPending} onClick={() => dismiss.mutate()}><CloseIcon width={16} height={16} /></button>
      {opened > 0 && (
        <button type="button" className="moment-head" onClick={() => void navigate({ to: "/clubs/$clubId/talk", params: { clubId }, search: { book: book.id, ...(moment.firstCommentId ? { at: moment.firstCommentId } : {}) } })}>
          <span className="faces">{moment.writers.slice(0, 3).map((w) => <Avatar key={w.id} name={w.name} image={w.image} />)}{moment.writers.length > 3 && <span className="avatar more">+{moment.writers.length - 3}</span>}</span>
          <span className="t">{txt.openedSince(what, since)}</span>
          <ChevronIcon width={16} height={16} className="go" />
        </button>
      )}
      {moment.landmarks.map((l) => <div key={l.id} className="moment-landmark">{txt.youVeReached(l.name)}</div>)}
      {opened === 0 && moment.notes.length === 0 && <span className="small muted">{txt.nothingNewOpenedSince(since)}</span>}
      {moment.notes.length > 0 && (
        <button type="button" className="moment-env" onClick={() => setEnvelopes(true)}>
          <Envelope count={moment.notes.length} />
          <span style={{ minWidth: 0 }}><b>{noteLine}</b><span className="d">{moment.notes.length === 1 ? txt.fromTapToOpen(moment.notes[0]!.author.name) : txt.tapToOpen}</span></span>
        </button>
      )}
      {envelopes && <EnvelopeSheet clubId={clubId} bookId={book.id} notes={moment.notes} ctx={ctx} onClose={() => setEnvelopes(false)} />}
    </section>
  );
}

/** The envelope lifts away and leaves the note (or the stack of notes). */
function EnvelopeSheet({ clubId, bookId, notes, ctx, onClose }: { clubId: string; bookId: string; notes: ForwardNote[]; ctx: PosCtx; onClose: () => void }) {
  const reduced = useReducedMotion();
  const [open, setOpen] = useState(reduced);
  useEffect(() => { if (!reduced) { const t = setTimeout(() => setOpen(true), 650); return () => clearTimeout(t); } }, [reduced]);
  return (
    <Sheet label={notes.length === 1 ? txt.aNoteForYou : txt.notesForYou(notes.length)} onClose={onClose}>
      {!open && <div className="env-open"><Envelope size="lg" /></div>}
      {open && <div className="stack-sm env-notes">{notes.map((n) => <PaperNote key={n.id} n={n} ctx={ctx} clubId={clubId} bookId={bookId} />)}</div>}
      {open && <p className="small muted center">{txt.keptInYourNotebook}</p>}
    </Sheet>
  );
}

/** A note on paper: one that reached you (share it with the club), or one of yours. */
export function PaperNote({ n, ctx, clubId, bookId }: { n: ForwardNote; ctx: PosCtx; clubId: string; bookId: string }) {
  const qc = useQueryClient();
  const share = useMutation({
    mutationFn: () => api.shareForwardNote(clubId, bookId, n.id),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["forward-notes", clubId, bookId] }), qc.invalidateQueries({ queryKey: ["discussion", clubId, bookId] }), qc.invalidateQueries({ queryKey: ["moment", clubId, bookId] })]),
  });
  const shared = Boolean(n.sharedCommentId) || share.isSuccess;
  return (
    <div className="paper-note">
      <div className="from">{txt.fromAt(n.author.name, spot(n.position, ctx), n.editedAt ? txt.edited : "")}</div>
      <p className="txt">{n.body}</p>
      {n.quote && <blockquote className="pq">“{n.quote}”</blockquote>}
      {!n.mine && (
        <div className="foot">
          <span>{n.arrivedAt ? txt.arrived(day(n.arrivedAt)) : ""}</span>
          {shared ? <span>{txt.sharedWithTheClub}</span>
            : <button type="button" className="paper-link" disabled={share.isPending} onClick={() => share.mutate()}>{txt.shareWithTheClub}</button>}
        </div>
      )}
      {share.isError && <p className="error small">{describeError(share.error)}</p>}
    </div>
  );
}

// ---- leaving a note forward

export function LeaveNoteSheet({ clubId, bookId, ctx, myPercent, onClose }: { clubId: string; bookId: string; ctx: PosCtx; myPercent: number; onClose: () => void }) {
  const qc = useQueryClient();
  const fn = useQuery({ queryKey: ["forward-notes", clubId, bookId], queryFn: () => api.forwardNotes(clubId, bookId) });
  const top = Math.max(1, myPercent);
  const [position, setPosition] = useState(top);
  const [forOne, setForOne] = useState(false);
  const [recipient, setRecipient] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const leave = useMutation({
    mutationFn: () => api.leaveForwardNote(clubId, bookId, { position, body, recipientId: forOne ? recipient : null }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["forward-notes", clubId, bookId] }); onClose(); },
  });
  const behind = (fn.data?.behind ?? []).filter((m) => m.percent < position);
  useEffect(() => { if (recipient && !behind.some((m) => m.id === recipient)) setRecipient(null); }, [position]); // eslint-disable-line react-hooks/exhaustive-deps
  const marks = (ctx.landmarks ?? []).filter((l) => l.percent >= 1 && l.percent <= top);
  const waiting = fn.data ? FORWARD_NOTE_LIMIT - fn.data.left : 0;
  const full = fn.data ? fn.data.left === 0 : false;
  const ready = body.trim().length > 0 && (!forOne || recipient) && !full;
  return (
    <Sheet label={txt.leaveANoteForward} onClose={onClose} className="leave-sheet">
      <p className="small muted" style={{ margin: 0 }}>{txt.itWaitsAtA}</p>
      <div className="field">
        <span>{txt.whereItWaits}</span>
        <div className="picker-line"><b>{posLabel(position, ctx)}</b></div>
        <div className="reach" style={{ "--you": `${top}%` } as React.CSSProperties}>
          <input type="range" min={0} max={100} step={1} value={position} aria-label={txt.whereItWaits} aria-valuetext={posLabel(position, ctx)} style={{ "--val": `${position}%` } as React.CSSProperties}
            onChange={(e) => setPosition(Math.max(1, Math.min(top, Number(e.target.value))))} />
          <span className="cap" aria-hidden="true" />
        </div>
        {marks.length > 0 && <div className="chips">{marks.map((l) => <button key={l.name} type="button" className="chip-btn" aria-pressed={position === l.percent} onClick={() => setPosition(l.percent)}>{l.name}</button>)}</div>}
        <span className="small muted">{txt.upToWhereYou(spot(top, ctx))}</span>
      </div>
      <div className="field">
        <span>{txt.whoItSFor}</span>
        <div className="seg" role="group" aria-label={txt.whoItSFor}>
          <button type="button" aria-pressed={!forOne} onClick={() => setForOne(false)}>{txt.everyoneWhoGetsThere}</button>
          <button type="button" aria-pressed={forOne} onClick={() => setForOne(true)}>{txt.onePerson}</button>
        </div>
        {forOne && (behind.length === 0 ? <span className="small muted">{txt.nobodyIsBehindRight(spot(position, ctx))}</span> : (
          <>
            <div className="people" role="radiogroup" aria-label={txt.whoItSFor}>
              {behind.map((m) => (
                <button key={m.id} type="button" role="radio" aria-checked={recipient === m.id} className="person" onClick={() => setRecipient(m.id)}>
                  <Avatar name={m.name} image={m.image} /><span className="n">{m.name}</span><span className="pos">{m.percent === 0 ? txt.notStarted : `${m.percent}%`}</span>
                </button>
              ))}
            </div>
            <span className="small muted">{txt.onlyPeopleWhoHaven(spot(position, ctx))}</span>
          </>
        ))}
      </div>
      <label className="field">
        <span>{txt.yourNote}</span>
        <textarea className="input book" rows={4} value={body} maxLength={FORWARD_NOTE_MAX_BODY} onChange={(e) => setBody(e.target.value)} placeholder={txt.whatShouldTheyKnow} />
        <span className="count">{txt.left(FORWARD_NOTE_MAX_BODY - body.length)}</span>
      </label>
      {leave.isError && <p className="error small">{describeError(leave.error)}</p>}
      <div className="leave-foot">
        {full ? <span className="small">{txt.ofYourNotesAre(FORWARD_NOTE_LIMIT, FORWARD_NOTE_LIMIT)}</span>
          : <span className="small muted">{txt.ofYourNotesWaiting(waiting, FORWARD_NOTE_LIMIT)}</span>}
        <button type="button" className={`btn ${full ? "" : "primary"}`} disabled={!ready || leave.isPending} onClick={() => leave.mutate()}>{txt.leaveNote}</button>
      </div>
    </Sheet>
  );
}

// ---- predictions

const forBook = (clubId: string, bookId: string) => ["predictions", clubId, bookId] as const;
export const usePredictions = (clubId: string, bookId: string | undefined, key = "") =>
  useQuery({ queryKey: [...forBook(clubId, bookId ?? ""), key], queryFn: () => api.predictions(clubId, bookId!), enabled: Boolean(bookId) });

/** On the Reading tab: the prompt while you're under the window, then your own, sealed. */
export function PredictionCard({ clubId, book, ctx, p }: { clubId: string; book: ClubBook; ctx: PosCtx; p: Predictions }) {
  const [editing, setEditing] = useState(false);
  const mine = p.mine;
  if (p.revealed) return null;
  if (p.canWrite && (!mine || editing)) return <PredictionForm clubId={clubId} bookId={book.id} ctx={ctx} p={p} onSaved={() => setEditing(false)} />;
  if (!mine) return null;
  const until = spot(p.window, ctx);
  return (
    <section className="card prediction">
      <div className="row" style={{ alignItems: "center" }}>
        <Envelope />
        <div className="grow" style={{ minWidth: 0 }}>
          <h3>{txt.yourPrediction}</h3>
          <div className="small muted">{mine.locked ? txt.sealedAtOpensWhen(spot(mine.sealedAtPercent, ctx)) : txt.sealedYouCanChange(until)}</div>
        </div>
        {!mine.locked && p.canWrite && <button type="button" className="btn ghost sm" onClick={() => setEditing(true)}>{txt.edit}</button>}
      </div>
      <p className="book muted pred-own">“{mine.body}”</p>
    </section>
  );
}

function PredictionForm({ clubId, bookId, ctx, p, onSaved }: { clubId: string; bookId: string; ctx: PosCtx; p: Predictions; onSaved?: () => void }) {
  const qc = useQueryClient();
  const [body, setBody] = useState(p.mine?.body ?? "");
  const [guess, setGuess] = useState<number | null>(p.mine?.ratingGuess ? p.mine.ratingGuess / 2 : null);
  const save = useMutation({
    mutationFn: () => api.savePrediction(clubId, bookId, { body, ratingGuess: guess ? Math.round(guess * 2) : null }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["predictions", clubId, bookId] }); onSaved?.(); },
  });
  return (
    <section className="card prediction prompt">
      <div className="row" style={{ alignItems: "center" }}>
        <Envelope />
        <div style={{ minWidth: 0 }}><h3>{p.mine ? txt.yourPrediction : txt.makeAPrediction}</h3><div className="small muted">{txt.beforeNobodyReadsIt(spot(p.window, ctx))}</div></div>
      </div>
      <textarea className="input book" rows={3} value={body} maxLength={PREDICTION_MAX_BODY} aria-label={txt.yourPrediction} placeholder={txt.whoMakesItTo} onChange={(e) => setBody(e.target.value)} />
      <div className="row spread" style={{ flexWrap: "wrap", gap: 8 }}>
        <span className="small muted row" style={{ gap: 8 }}>{txt.iThinkILl}<StarPicker value={guess} onChange={setGuess} /></span>
        <button type="button" className="btn primary sm" disabled={!body.trim() || save.isPending} onClick={() => save.mutate()}>{p.mine ? txt.sealItAgain : txt.sealIt}</button>
      </div>
      {save.isError && <p className="error small">{describeError(save.error)}</p>}
    </section>
  );
}

/** For Talk's + menu: the same form in a sheet. */
export function PredictionSheet({ clubId, bookId, ctx, onClose }: { clubId: string; bookId: string; ctx: PosCtx; onClose: () => void }) {
  const p = usePredictions(clubId, bookId);
  return (
    <Sheet label={txt.makeAPrediction} onClose={onClose}>
      {p.data && (p.data.canWrite ? <PredictionForm clubId={clubId} bookId={bookId} ctx={ctx} p={p.data} onSaved={onClose} />
        : <p className="small muted">{txt.predictionsCloseAt(spot(p.data.window, ctx))}</p>)}
    </Sheet>
  );
}

/** "4 predictions sealed", with who wrote one and never what; once you're done, a way to read them. */
export function PredictionsRow({ ctx, p }: { ctx: PosCtx; p: Predictions }) {
  const [reading, setReading] = useState(false);
  if (p.all.length === 0) return null;
  const faces = <span className="faces">{p.all.slice(0, 5).map((x) => <Avatar key={x.author.id} name={x.author.name} image={x.author.image} />)}</span>;
  if (!p.revealed) return (
    <div className="quiet preds-row">
      <Envelope count={p.all.length} />
      <div style={{ minWidth: 0 }}><b>{txt.sealed(plural(p.all.length, txt.prediction, txt.predictions))}</b><div className="d">{faces}</div></div>
    </div>
  );
  return (
    <>
      <button type="button" className="quiet link preds-row" onClick={() => setReading(true)}>
        <Envelope count={p.all.length} />
        <div style={{ minWidth: 0 }}><b>{plural(p.all.length, txt.prediction, txt.predictions)}</b><div className="d">{txt.openedReadThemAgain}</div></div>
        <ChevronIcon className="go" />
      </button>
      {reading && <Sheet label={txt.thePredictions} onClose={() => setReading(false)}><PredictionList all={p.all} ctx={ctx} /></Sheet>}
    </>
  );
}

/** Guesses on paper, each beside the real rating. Nothing is ranked or marked right. `open` staggers them in. */
export function PredictionList({ all, ctx, open = all.length, grid }: { all: Prediction[]; ctx: PosCtx; open?: number; grid?: boolean }) {
  return (
    <div className={grid ? "pred-grid" : "pred-list"}>
      {all.map((x, i) => i < open ? (
        <div key={x.author.id} className="paper-note pred-open">
          <div className="who"><Avatar name={x.author.name} image={x.author.image} /><b>{x.mine ? txt.you : x.author.name}</b>{txt.sealedAt(spot(x.sealedAtPercent, ctx))}</div>
          <p className="txt">{x.body}</p>
          <div className="ratings">
            {x.ratingGuess !== null && <span>{txt.guessed}<Stars value={x.ratingGuess / 2} size="sm" /></span>}
            <span>{x.rating !== null ? <>{txt.gaveIt}<Stars value={x.rating / 2} size="sm" /></> : txt.hasnTRatedYet}</span>
          </div>
        </div>
      ) : (
        <div key={x.author.id} className="pred-sealed"><Envelope /><span className="small">{txt.sealedAt2(x.mine ? txt.you : x.author.name, spot(x.sealedAtPercent, ctx))}</span></div>
      ))}
    </div>
  );
}

// ---- finishing the book

/**
 * At 100%: the book closes, "Everything's open" with what opened, then the predictions one by one. Rating and
 * a first reaction come after, as a quiet button, so the ceremony isn't a form.
 */
export function FinishCeremony({ clubId, book, ctx, moment, p }: { clubId: string; book: ClubBook; ctx: PosCtx; moment: Moment | null; p: Predictions | undefined }) {
  const reduced = useReducedMotion();
  const [opened, setOpened] = useState(0);
  const [rating, setRating] = useState(false);
  const preds = p?.all ?? [];
  const opening = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => () => { if (opening.current) clearInterval(opening.current); }, []);
  const openAll = () => {
    if (reduced) { setOpened(preds.length); return; }
    setOpened(1);
    opening.current = setInterval(() => setOpened((n) => { if (n + 1 >= preds.length && opening.current) clearInterval(opening.current); return n + 1; }), 450);
  };
  const qc = useQueryClient();
  // "Not now": the ceremony is done; rating and a reaction can wait for the Shelf.
  const later = useMutation({
    mutationFn: async () => { await api.finishLine(clubId, book.id, { stars: null, reaction: null }); await api.dismissMoment(clubId, book.id); },
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["books", clubId] }), qc.invalidateQueries({ queryKey: ["moment", clubId, book.id] })]),
  });
  const f = moment?.finished;
  return (
    <section className="card ceremony">
      <div className="ceremony-book"><Cover book={book.book} size="lg" /></div>
      <h2>{txt.everythingSOpen}</h2>
      <p className="muted small center" style={{ marginTop: -6 }}>{txt.youFinished(book.book.title)}</p>
      {/* Only what there is: a zero would read as a loss. */}
      {((f?.messages ?? 0) > 0 || (f?.reactions ?? 0) > 0 || preds.length > 0) && (
        <div className="counts">
          {f && f.messages > 0 && <div><b>{f.messages}</b><span>{f.messages === 1 ? txt.message : txt.messages}</span></div>}
          {f && f.reactions > 0 && <div><b>{f.reactions}</b><span>{f.reactions === 1 ? txt.firstReaction : txt.firstReactions}</span></div>}
          {preds.length > 0 && <div><b>{preds.length}</b><span>{preds.length === 1 ? txt.prediction : txt.predictions}</span></div>}
        </div>
      )}
      {preds.length > 0 && opened === 0 && <button type="button" className="btn primary" style={{ alignSelf: "center" }} onClick={openAll}>{txt.openThePredictions}</button>}
      {opened > 0 && <PredictionList all={preds} ctx={ctx} open={opened} />}
      {!rating ? (
        <div className="row" style={{ justifyContent: "center", flexWrap: "wrap", gap: 4 }}>
          <button type="button" className="btn ghost sm" onClick={() => setRating(true)}>{txt.rateItAndAdd}</button>
          <button type="button" className="btn ghost sm" disabled={later.isPending} onClick={() => later.mutate()}>{txt.notNow}</button>
        </div>
      ) : <FinishLineCard clubId={clubId} book={book} embedded />}
    </section>
  );
}
